import React, {useEffect, useState} from 'react';
import { Warning, CaretRight, Cloud, CloudRain, CloudSnow, MapPin, Moon, Sun } from '@phosphor-icons/react';
import {getWeatherForAddress, getForecastForAddress, type WeatherData, type ForecastData} from '@/lib/weather';
import {useOrganization} from '@/hooks/useQueries';
import {WeatherDetailsModal} from './weather-details-modal';
import {useWeatherAlerts} from '@/components/dispatch/WeatherAlertBanner';
import {SidebarMenuButton} from '@/components/ui/sidebar';
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip';

// Map weather icons to Phosphor icons
const getWeatherIcon = (condition: string, iconCode?: string) => {
  const lowerCondition = condition.toLowerCase();
  // OpenWeatherMap icon codes carry a day/night suffix (e.g. '01d' vs '01n').
  const isNight = iconCode?.endsWith('n') ?? false;
  const clearIcon = isNight
    ? <Moon weight="fill" className="w-4 h-4 text-stone-200" />
    : <Sun className="w-4 h-4 text-orange-300" />;

  if (lowerCondition.includes('sunny') || lowerCondition.includes('clear')) {
    return clearIcon;
  } else if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle')) {
    return <CloudRain className="w-4 h-4 text-stone-300" />;
  } else if (lowerCondition.includes('snow')) {
    return <CloudSnow className="w-4 h-4 text-stone-200" />;
  } else if (lowerCondition.includes('cloud')) {
    return <Cloud className="w-4 h-4 text-stone-300" />;
  } else {
    return clearIcon;
  }
};

interface WeatherWidgetProps {
  className?: string;
  showLocation?: boolean;
  variant?: 'default' | 'compact' | 'inline';
  /** Controlled details modal — used when a parent menu opens weather. */
  detailsOpen?: boolean;
  onDetailsOpenChange?: (open: boolean) => void;
  /** Hide the inline/compact trigger; pair with controlled detailsOpen. */
  hideTrigger?: boolean;
}

export function WeatherWidget({
  className = '',
  showLocation = true,
  variant = 'default',
  detailsOpen,
  onDetailsOpenChange,
  hideTrigger = false,
}: WeatherWidgetProps) {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [uncontrolledDetailsOpen, setUncontrolledDetailsOpen] = useState(false);
  const showDetailsModal = detailsOpen ?? uncontrolledDetailsOpen;
  const setShowDetailsModal = onDetailsOpenChange ?? setUncontrolledDetailsOpen;
  const weatherAlerts = useWeatherAlerts();
  const hasAlerts = weatherAlerts.length > 0;
  
  // Use TanStack Query hook for organization data
  const { data: organizationResponse } = useOrganization();
  const companyAddress = organizationResponse?.organization?.companyAddress || '';

  useEffect(() => {
    loadWeatherData();
  }, [companyAddress]);

  useEffect(() => {
    // Listen for organization settings updates
    const handleSettingsUpdate = () => {
      loadWeatherData();
    };
    
    window.addEventListener('organization-settings-updated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('organization-settings-updated', handleSettingsUpdate);
    };
  }, []);

  async function loadWeatherData() {
    try {
      setLoading(true);

      const address = companyAddress;

      if (!address) {
        setShowAddressWarning(true);
        setWeather({
          temperature: 72,
          condition: 'Sunny',
          icon: '01d',
          city: 'Unknown',
          state: 'Unknown',
          error: 'No company address set',
        });
        return;
      }

      setShowAddressWarning(false);

      // Fetch weather and forecast for the company address
      const weatherData = await getWeatherForAddress(address);
      setWeather(weatherData);

      const forecastData = await getForecastForAddress(address, weatherData.condition);
      setForecast(forecastData);

    } catch (error) {
      console.error('Error loading weather data:', error);
      setWeather({
        temperature: 72,
        condition: 'Sunny',
        icon: '01d',
        city: 'Unknown',
        state: 'Unknown',
        error: 'Failed to load weather',
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    // Parent menu owns the entry point — stay silent until data is ready.
    if (hideTrigger) return null;

    if (variant === 'compact') {
      return (
        <div className={`flex flex-col items-center justify-center gap-0.5 text-white ${className}`}>
          <Sun className="w-4 h-4 text-orange-300 animate-pulse" />
          <span className="text-[10px] font-medium">--°</span>
        </div>
      );
    }

    if (variant === 'inline') {
      return (
        <div className={`flex items-center gap-1.5 text-white/70 ${className}`}>
          <Sun className="w-4 h-4 text-orange-300 animate-pulse" />
          <span className="text-xs font-medium">--°</span>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-2 text-white group-hover:text-black transition-colors ${className}`}>
        <Sun className="w-4 h-4 text-orange-300 animate-pulse" />
        <span className="text-sm font-medium">--°F</span>
        <span className="text-xs text-white/70 group-hover:text-black/70">Loading...</span>
      </div>
    );
  }

  if (!weather) {
    return null;
  }

  // Compact variant - icon only
  if (variant === 'compact') {
    return (
      <>
        {!hideTrigger && (
          <div className="relative">
            <SidebarMenuButton
              tooltip={`Weather: ${weather.temperature}°F ${weather.condition}`}
              onClick={() => setShowDetailsModal(true)}
              className={`hover:!bg-white hover:!text-black ${className}`}
            >
              {showAddressWarning ? (
                <Warning className="h-4 w-4 text-orange-300" />
              ) : (
                getWeatherIcon(weather.condition, weather.icon)
              )}
            </SidebarMenuButton>
            {hasAlerts && (
              <CloudSnow className="absolute -top-0.5 -right-0.5 h-3 w-3 text-orange-400 pointer-events-none" />
            )}
          </div>
        )}

        <WeatherDetailsModal
          open={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          weather={weather}
          forecast={forecast}
          companyAddress={companyAddress}
          weatherAlerts={weatherAlerts}
        />
      </>
    );
  }

  // Inline variant - icon + temp for the page-header utility cluster
  if (variant === 'inline') {
    return (
      <>
        {!hideTrigger && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setShowDetailsModal(true)}
                className={`relative h-9 px-2 hover:bg-white/10 rounded-md transition-colors text-white/70 hover:text-white flex items-center gap-1.5 ${className}`}
              >
                {showAddressWarning ? (
                  <Warning className="h-4 w-4 text-orange-300" />
                ) : (
                  getWeatherIcon(weather.condition, weather.icon)
                )}
                <span className="text-xs font-medium">{weather.temperature}°</span>
                {hasAlerts && (
                  <CloudSnow className="absolute -top-1 -right-1.5 h-3 w-3 text-orange-400" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {hasAlerts ? 'Snowfall forecast alert' : `${weather.condition} — ${weather.city}`}
            </TooltipContent>
          </Tooltip>
        )}

        <WeatherDetailsModal
          open={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          weather={weather}
          forecast={forecast}
          companyAddress={companyAddress}
          weatherAlerts={weatherAlerts}
        />
      </>
    );
  }

  // Default variant
  return (
    <>
      <button
        onClick={() => setShowDetailsModal(true)}
        className={`flex items-center gap-3 cursor-pointer ${className}`}
      >
        {/* Weather icon */}
        {showAddressWarning ? (
          <Warning className="w-4 h-4 text-orange-300" />
        ) : (
          getWeatherIcon(weather.condition, weather.icon)
        )}

        {/* Temperature and condition */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {weather.temperature}°F
          </span>
          <span className="text-xs opacity-90">
            {weather.condition}
          </span>
        </div>

        {/* Location (optional) */}
        {showLocation && weather.city && weather.city !== 'Unknown' && (
          <div className="flex items-center gap-1 text-xs opacity-70 border-l border-white/20 pl-3">
            <MapPin className="w-3 h-3" />
            <span>{weather.city}</span>
          </div>
        )}

        {weather.error && (
          <span className="text-xs text-orange-300" title={weather.error}>
            ⚠️
          </span>
        )}

        {/* Affordance that the row opens the weather details */}
        <CaretRight className="w-4 h-4 ml-auto opacity-50" />
      </button>

      <WeatherDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        weather={weather}
        forecast={forecast}
        companyAddress={companyAddress}
      />
    </>
  );
}

// Hook for other components to get weather data
export function useWeatherData() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Use TanStack Query hook for organization data
  const { data: organizationResponse } = useOrganization();

  useEffect(() => {
    async function loadWeather() {
      try {
        const address = organizationResponse?.organization?.companyAddress || '';

        if (address) {
          const weatherData = await getWeatherForAddress(address);
          setWeather(weatherData);
        }
      } catch (error) {
        console.error('Error loading weather:', error);
      } finally {
        setLoading(false);
      }
    }

    if (organizationResponse?.organization) {
      loadWeather();
    }
  }, [organizationResponse]);

  return { weather, loading };
}
